import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";

type QueueRow = {
  id: number;
  issue: string | null;
  tier1Issue: string | null;
  serial: string | null;
  locationName: string | null;
};

const STAGE_LABEL: Record<string, string> = {
  awaiting_tier_1: "Awaiting tier 1",
  tier_1_in_progress: "Tier 1 in progress",
  tier_2: "Escalated to tier 2",
  out_on_loan: "Out on loan",
  resolved: "Resolved",
};

export function tierReviewStageLabel(stage: string): string {
  return STAGE_LABEL[stage] ?? stage;
}

export function tierReviewStageTone(stage: string): BadgeTone {
  if (stage === "resolved") return "success";
  if (stage === "out_on_loan" || stage === "tier_2") return "warning";
  if (stage === "tier_1_in_progress") return "info";
  return "neutral";
}

export function TierReviewStageBadge({ stage }: { stage: string }) {
  return <Badge tone={tierReviewStageTone(stage)}>{tierReviewStageLabel(stage)}</Badge>;
}

const TIER_REVIEW_CHAIN = [
  { key: "tier_1", label: "Tier 1", stages: ["awaiting_tier_1", "tier_1_in_progress"] },
  { key: "tier_2", label: "Tier 2", stages: ["tier_2"] },
  { key: "resolved", label: "Resolved", stages: ["resolved"] },
] as const;

const TIER_REVIEW_OPTIONAL = [
  { key: "analysis_loan", label: "Analysis loan", stages: ["out_on_loan"] },
] as const;

function progressTone(stage: string): BadgeTone {
  if (stage === "resolved") return "success";
  if (stage === "out_on_loan" || stage === "tier_2") return "warning";
  return "info";
}

export function TierReviewProgress({ stage }: { stage: string }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Review status">
      {TIER_REVIEW_CHAIN.map((step, index) => {
        const current = (step.stages as readonly string[]).includes(stage);
        return (
          <span key={step.key} className="inline-flex items-center gap-1.5">
            {index > 0 ? <span className="text-sm text-slate-400" aria-hidden>{">"}</span> : null}
            <Badge tone={current ? progressTone(stage) : "muted"} className={current ? undefined : "opacity-70"}>
              <span aria-current={current ? "step" : undefined}>{step.label}</span>
            </Badge>
          </span>
        );
      })}
      {TIER_REVIEW_OPTIONAL.map((step) => {
        const current = (step.stages as readonly string[]).includes(stage);
        return (
          <span key={step.key} className="inline-flex items-center gap-1.5">
            <span className="text-sm text-slate-400" aria-hidden>{"|"}</span>
            <Badge tone={current ? progressTone(stage) : "muted"} className={current ? undefined : "opacity-70"}>
              <span aria-current={current ? "step" : undefined}>{step.label}</span>
            </Badge>
          </span>
        );
      })}
    </div>
  );
}

export function QualityTierReviewQueuePage({ tier }: { tier: 1 | 2 }) {
  const navigate = useNavigate();
  const [locationId, setLocationId] = useState("");
  const locations = useQuery({
    queryKey: ["quality", "tier-review-locations"],
    queryFn: () => apiFetch<{ rows: { id: number; name: string }[] }>("/api/quality/tier-reviews/locations"),
  });
  const queue = useQuery({
    queryKey: ["quality", "tier-reviews", tier, locationId],
    queryFn: () => {
      const params = new URLSearchParams({ tier: String(tier) });
      if (locationId !== "") params.set("locationId", locationId);
      return apiFetch<{ rows: QueueRow[] }>(`/api/quality/tier-reviews?${params.toString()}`);
    },
  });
  const columns = useMemo<HubDataTableColumn<QueueRow>[]>(
    () => [
      { id: "id", header: "NCR", sortValue: (row) => row.id, cell: (row) => `NCR ${row.id}` },
      { id: "serial", header: "Serial", sortValue: (row) => row.serial ?? "", cell: (row) => row.serial ?? "" },
      { id: "issue", header: "Issue", sortValue: (row) => row.issue ?? "", cell: (row) => row.issue ?? "" },
      {
        id: "tier1Issue",
        header: "Tier 1 issue",
        sortValue: (row) => row.tier1Issue ?? "",
        cell: (row) => row.tier1Issue ?? "",
      },
      {
        id: "locationName",
        header: "Location",
        sortValue: (row) => row.locationName ?? "",
        cell: (row) => row.locationName ?? "",
      },
    ],
    [],
  );
  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <PageTitleWithHelp hoverHint={tier === 1 ? "Open NCRs that do not yet have a completed tier 1 review." : "Open NCRs whose tier 1 review escalated them."}>
          {tier === 1 ? "Tier 1 review" : "Tier 2 review"}
        </PageTitleWithHelp>
        <label className="text-sm text-slate-700">
          Location
          <select className="ml-2 rounded border border-slate-300 px-2 py-1" value={locationId} onChange={(event) => setLocationId(event.target.value)}>
            <option value="">All locations</option>
            {(locations.data?.rows ?? []).map((location) => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
        </label>
      </header>
      <HubDataTable
        rows={queue.data?.rows ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={queue.isLoading}
        error={queue.error instanceof Error ? queue.error.message : null}
        empty="No NCRs in this queue."
        onRowClick={(row) => navigate(`/quality/tier-${tier}-review/${row.id}`)}
        defaultSortKey="id"
        defaultSortDir="desc"
      />
    </div>
  );
}
